import { ONBOARDING_COPY } from '@/locales/en'
import type { UsagePolicy } from '@/common/types'

export type ResolvedCheckboxItem = {
    id: string
    label: string
    updatedAt?: string
}

export type ResolvedSection = {
    id: string
    title: string
    description?: string
    checkboxes: ResolvedCheckboxItem[]
    slugs: string[]
}

export type ResolvedTermsSections = {
    sections: ResolvedSection[]
    legacyPolicies: UsagePolicy[]
}

/** Not backed by any policy slug — this agreement always exists in the new design,
 *  independent of what the API returns, so it is prepended unconditionally. */
function buildChessSponsorshipSection(): ResolvedSection {
    const { chessSponsorship } = ONBOARDING_COPY.termsAndConditions.sections

    return {
        id: 'chess-sponsorship',
        title: chessSponsorship.title,
        description: chessSponsorship.description,
        checkboxes: [{ id: 'chess-sponsorship', label: chessSponsorship.checkboxLabel }],
        slugs: [],
    }
}

/** Hardcoded content (title/description/checkbox come from locale, not the API's disclaimer/
 *  checkboxDisclaimer fields) — only shown when BOTH finclear-terms and cashhub-terms are
 *  present. Otherwise those policies fall through to legacyPolicies unchanged. */
function buildFinclearCashHubSection(policies: UsagePolicy[]): ResolvedSection | null {
    const hasFinclear = policies.some((policy) => policy.slug === 'finclear-terms')
    const hasCashhub = policies.some((policy) => policy.slug === 'cashhub-terms')

    if (!hasFinclear || !hasCashhub) return null

    const { finclearCashhub } = ONBOARDING_COPY.termsAndConditions.sections

    return {
        id: 'finclear-cashhub',
        title: finclearCashhub.title,
        description: finclearCashhub.description,
        checkboxes: [{ id: 'finclear-cashhub', label: finclearCashhub.checkboxLabel }],
        slugs: ['finclear-terms', 'cashhub-terms'],
    }
}

/** Title/description are hardcoded from locale — only the checkbox comes from the actual
 *  monoova-terms policy returned by the request. */
function buildMonoovaSection(policies: UsagePolicy[]): ResolvedSection | null {
    const monoovaPolicy = policies.find((policy) => policy.slug === 'monoova-terms')
    if (!monoovaPolicy) return null

    const { monoova } = ONBOARDING_COPY.termsAndConditions.sections

    return {
        id: 'monoova',
        title: monoova.title,
        description: monoova.description,
        checkboxes: [{ id: 'monoova', label: monoovaPolicy.disclaimer, updatedAt: monoovaPolicy.updatedAt }],
        slugs: ['monoova-terms'],
    }
}

/** Title/description are hardcoded from locale. adviser-terms (present only on adviser-created
 *  accounts) has no section of its own, so its checkbox is folded into this section rather than
 *  falling through to legacyPolicies, where it would be accepted on submit without ever being
 *  shown to the user. The section renders as long as either policy is present — six-terms is
 *  not guaranteed to be present alongside adviser-terms. */
function buildInformedConsentSection(policies: UsagePolicy[]): ResolvedSection | null {
    const sixTermsPolicy = policies.find((p) => p.slug === 'six-terms')
    const adviserPolicy = policies.find((p) => p.slug === 'adviser-terms')
    if (!sixTermsPolicy && !adviserPolicy) return null

    const { informedConsent } = ONBOARDING_COPY.termsAndConditions.sections

    const checkboxes: ResolvedCheckboxItem[] = []
    if (sixTermsPolicy) {
        checkboxes.push({
            id: 'informed-consent',
            label: sixTermsPolicy.disclaimer,
            updatedAt: sixTermsPolicy.updatedAt,
        })
    }
    if (adviserPolicy) {
        checkboxes.push({
            id: 'adviser-terms-checkbox',
            label: adviserPolicy.disclaimer,
            updatedAt: adviserPolicy.updatedAt,
        })
    }

    const slugs: string[] = []
    if (sixTermsPolicy) slugs.push('six-terms')
    if (adviserPolicy) slugs.push('adviser-terms')

    return {
        id: 'informed-consent',
        title: informedConsent.title,
        description: informedConsent.description,
        checkboxes,
        slugs,
    }
}

/** Multi-checkbox section: the FinClear terms checkbox (dynamic) always shows once
 *  finclear-terms is present; the CashHub terms checkbox (dynamic) and the hardcoded
 *  bank-account authorisation checkbox only appear alongside it when cashhub-terms is
 *  also present. Each checkbox tracks its own independent checked state. */
function buildFinclearConsentSection(policies: UsagePolicy[]): ResolvedSection | null {
    const finclearPolicy = policies.find((policy) => policy.slug === 'finclear-terms')
    if (!finclearPolicy) return null

    const cashhubPolicy = policies.find((policy) => policy.slug === 'cashhub-terms')
    const { finclear } = ONBOARDING_COPY.termsAndConditions.sections

    const checkboxes: ResolvedCheckboxItem[] = [
        {
            id: 'finclear-terms-checkbox',
            label: finclearPolicy.disclaimer,
            updatedAt: finclearPolicy.updatedAt,
        },
    ]

    if (cashhubPolicy) {
        checkboxes.push({
            id: 'cashhub-terms-checkbox',
            label: cashhubPolicy.disclaimer,
            updatedAt: cashhubPolicy.updatedAt,
        })
        checkboxes.push({ id: 'cashhub-bank-authorisation', label: finclear.checkboxLabel })
    }

    return {
        id: 'finclear-consent',
        title: finclear.title,
        checkboxes,
        slugs: cashhubPolicy ? ['finclear-terms', 'cashhub-terms'] : ['finclear-terms'],
    }
}

/** Read-only — no checkbox. Renders the raw FrankieOne disclaimer as the section description. */
function buildFrankieOneConsentSection(policies: UsagePolicy[]): ResolvedSection | null {
    const policy = policies.find((p) => p.slug === 'frankieone-terms')
    if (!policy) return null

    const { frankieOne } = ONBOARDING_COPY.termsAndConditions.sections

    return {
        id: 'frankieone-consent',
        title: frankieOne.title,
        description: policy.disclaimer,
        checkboxes: [],
        slugs: ['frankieone-terms'],
    }
}

/** Pure grouping/title-resolution logic — takes API policies, returns display-ready sections.
 *  Any slug not consumed by a section (hardcoded or policy-driven) falls through to
 *  legacyPolicies so it still renders. */
export function resolveTermsSections(policies: UsagePolicy[]): ResolvedTermsSections {
    const finclearCashHubSection = buildFinclearCashHubSection(policies)
    const monoovaSection = buildMonoovaSection(policies)
    const informedConsentSection = buildInformedConsentSection(policies)
    const finclearConsentSection = buildFinclearConsentSection(policies)
    const frankieOneConsentSection = buildFrankieOneConsentSection(policies)

    const sections: ResolvedSection[] = [buildChessSponsorshipSection()]
    if (finclearCashHubSection) sections.push(finclearCashHubSection)
    if (monoovaSection) sections.push(monoovaSection)
    if (informedConsentSection) sections.push(informedConsentSection)
    if (finclearConsentSection) sections.push(finclearConsentSection)
    if (frankieOneConsentSection) sections.push(frankieOneConsentSection)

    const consumedSlugs = new Set<string>(sections.flatMap((section) => section.slugs))
    const legacyPolicies = policies.filter((policy) => !consumedSlugs.has(policy.slug))

    return { sections, legacyPolicies }
}
